import type { ProviderClaimResponsibilityReportItem } from '@enter/core';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import {
    DEFAULT_RESPONSIBILITY_PRESET,
    FixedResponsibilityPreset,
    formatResponsibilityRange,
    IDateOnlyRange,
    isDateOnly,
    localToday,
    presetRange,
    ResponsibilityRangePreset,
    responsibilityRangeInputLimits,
    toResponsibilityReportParams,
    validateResponsibilityRange,
} from './claim-responsibility-range.util';
import {
    IRankedShare,
    IResponsibilityCategoryTotal,
    IResponsibilityGroupTotal,
    IResponsibilitySummary,
    rankByMetric,
    ResponsibilityMetric,
    ResponsibilityType,
    summarizeResponsibilityReport,
} from './claim-responsibility-report.util';
import type { IFeatures } from './root.store';

export type ClaimResponsibilityReportFetcher = (params: {
    start: string;
    end: string;
}) => Promise<ProviderClaimResponsibilityReportItem[] | undefined>;

/**
 * State of the "Active Claims By Responsibility" widget on the provider Claims page (FE-151).
 *
 * The fetcher is injected rather than imported so this store has no runtime dependency on the
 * generated client and can be unit-tested without a DOM. `RootStore` passes
 * `ClaimResponsibilityService.fetchReport`.
 *
 * Three states, never conflated (NS-3): loading (the last render stays on screen), loaded (an
 * empty report is a real `$0 · 0 claims`), and error (no figures at all — a failed request is not a
 * zero).
 */
export class ClaimResponsibilityStore {
    @observable public preset: ResponsibilityRangePreset = DEFAULT_RESPONSIBILITY_PRESET;
    /** The range the widget is asking for. Always valid; an invalid custom edit never reaches it. */
    @observable public range: IDateOnlyRange;
    /** What the custom date inputs hold, which may be mid-edit and invalid. */
    @observable public customStart: string;
    @observable public customEnd: string;
    /** Why the custom range was not sent. Shown under the inputs. */
    @observable public rangeError: string | undefined;

    @observable public metric: ResponsibilityMetric = 'charges';
    @observable public selectedType: ResponsibilityType | undefined;
    /** A raw category code. Only meaningful together with `selectedType`. */
    @observable public selectedCategory: string | undefined;

    @observable.ref public items: ProviderClaimResponsibilityReportItem[] = [];
    /** The range the items on screen came from — the header cannot describe one range with another's data. */
    @observable public loadedRange: IDateOnlyRange | undefined;
    @observable public loading: boolean = false;
    @observable public loaded: boolean = false;
    @observable public error: string | undefined;

    private features: IFeatures;
    private fetchReport: ClaimResponsibilityReportFetcher;
    /** The demo source: the same report, built from the demo claims (NS-14 — nothing real renders). */
    private generateReport: ClaimResponsibilityReportFetcher | undefined;
    private today: () => string;
    /** Which load is the latest. A slower response for a range already left behind must not overwrite it. */
    private requestId = 0;

    constructor(
        features: IFeatures,
        fetchReport: ClaimResponsibilityReportFetcher,
        today: () => string = () => localToday(),
        generateReport?: ClaimResponsibilityReportFetcher,
    ) {
        this.features = features;
        this.fetchReport = fetchReport;
        this.generateReport = generateReport;
        this.today = today;
        this.range = presetRange(DEFAULT_RESPONSIBILITY_PRESET, today());
        this.customStart = this.range.start;
        this.customEnd = this.range.end;
        makeObservable(this);
    }

    @computed public get summary(): IResponsibilitySummary {
        return summarizeResponsibilityReport(this.items);
    }

    /** Main bar and chips: every group except None, by the selected metric. */
    @computed public get rankedGroups(): IRankedShare<IResponsibilityGroupTotal>[] {
        return rankByMetric(this.summary.activeWork, this.metric);
    }

    @computed public get selectedGroup(): IResponsibilityGroupTotal | undefined {
        return this.selectedType ? this.summary.groups[this.selectedType] : undefined;
    }

    /** The category breakdown of the selected group — None's included when None is selected. */
    @computed public get rankedCategories(): IRankedShare<IResponsibilityCategoryTotal>[] {
        return this.selectedGroup ? rankByMetric(this.selectedGroup.categories, this.metric) : [];
    }

    /**
     * How many claims the current selection covers, by the report (crosswalk §8): the category's
     * count, else the group's, else all active work (None excluded). `undefined` while there is no
     * report to say — unknown is not zero.
     */
    @computed public get selectionCount(): number | undefined {
        if (!this.loadedRange) {
            return undefined;
        }
        if (this.selectedGroup && this.selectedCategory) {
            return this.selectedGroup.categories.find(c => c.code === this.selectedCategory)?.count ?? 0;
        }
        if (this.selectedType) {
            return this.selectedGroup?.count ?? 0;
        }
        return this.summary.activeWorkTotal.count;
    }

    /** `Oct 1, 2025 – Sep 30, 2026` for the range button. */
    @computed public get rangeLabel(): string {
        return formatResponsibilityRange(this.range);
    }

    @computed public get inputLimits(): { startMax: string; endMin: string; endMax: string } {
        // Mid-edit the start input can be empty; the limits then follow the range still in effect.
        const start = isDateOnly(this.customStart) ? this.customStart : this.range.start;
        return responsibilityRangeInputLimits(start, this.today());
    }

    @action
    public setPreset = (preset: FixedResponsibilityPreset): void => {
        this.preset = preset;
        this.applyRange(presetRange(preset, this.today()));
    };

    @action
    public setCustomStart = (start: string): void => {
        this.setCustomRange(start, this.customEnd);
    };

    @action
    public setCustomEnd = (end: string): void => {
        this.setCustomRange(this.customStart, end);
    };

    @action
    public setMetric = (metric: ResponsibilityMetric): void => {
        this.metric = metric;
    };

    /** Click a chip, bar segment or the None pill: select it, or clear it if it is already selected. */
    @action
    public toggleType = (type: ResponsibilityType): void => {
        this.selectedType = this.selectedType === type ? undefined : type;
        this.selectedCategory = undefined;
    };

    @action
    public toggleCategory = (code: string): void => {
        if (!this.selectedType) {
            return;
        }
        this.selectedCategory = this.selectedCategory === code ? undefined : code;
    };

    /** Set both at once — for mirroring filters changed elsewhere, e.g. a chip removed from the table. */
    @action
    public setSelection = (type: ResponsibilityType | undefined, category?: string): void => {
        this.selectedType = type;
        this.selectedCategory = type ? category : undefined;
    };

    @action
    public clearSelection = (): void => {
        this.setSelection(undefined);
    };

    @action
    public load = async (): Promise<void> => {
        // A preset is relative to today. Recompute it so a page left open past midnight does not
        // keep asking for a window that now ends the day before yesterday.
        if (this.preset !== 'custom') {
            const range = presetRange(this.preset, this.today());
            this.range = range;
            this.customStart = range.start;
            this.customEnd = range.end;
        }

        const range = this.range;
        const request = ++this.requestId;

        // In demo mode the API is never asked (NS-14): the report comes from the demo claims, the same
        // claims the table under the widget lists. Without a demo source, say so rather than letting
        // the boundary's refusal read as a broken widget.
        const source = this.features.isDemo ? this.generateReport : this.fetchReport;
        if (!source) {
            this.items = [];
            this.loadedRange = undefined;
            this.loading = false;
            this.loaded = true;
            this.error = 'Claims by responsibility are not generated in demo mode yet.';
            return;
        }

        this.loading = true;
        this.error = undefined;

        try {
            const items = await source(toResponsibilityReportParams(range));
            if (request !== this.requestId) {
                return;
            }
            runInAction(() => {
                this.items = items ?? [];
                this.loadedRange = range;
                this.loaded = true;
                this.loading = false;
            });
        } catch (err) {
            if (request !== this.requestId) {
                return;
            }
            runInAction(() => {
                // The figures described a range that is no longer what the widget shows, and a failed
                // request is unknown, not zero — clear them rather than leave them looking current (NS-3).
                this.items = [];
                this.loadedRange = undefined;
                this.error = err instanceof Error && err.message ? err.message : 'Failed to load claims by responsibility';
                this.loaded = true;
                this.loading = false;
            });
        }
    };

    @action
    private setCustomRange(start: string, end: string): void {
        this.preset = 'custom';
        this.customStart = start;
        this.customEnd = end;
        const error = validateResponsibilityRange({ start, end }, this.today());
        if (error) {
            // The previous figures stay up; only the selection goes, as for any range change.
            this.rangeError = error;
            this.clearSelection();
            return;
        }
        this.applyRange({ start, end });
    }

    @action
    private applyRange(range: IDateOnlyRange): void {
        this.range = range;
        this.customStart = range.start;
        this.customEnd = range.end;
        this.rangeError = undefined;
        // A selection belongs to the old range's report; the group may not exist in the new one.
        this.clearSelection();
        void this.load();
    }
}
